import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { leadDetailsRouteUrl, links } from 'utils/featureToggles'

import LeadActivitiesPage from './LeadActivitiesPage'
import LeadDemoWelcomeAccountsPage from './LeadDemoWelcomeAccountsPage'
import LeadDetailsNotesPage from './LeadDetailsNotesPage'
import LeadDetailsPage from './LeadDetailsPage'

export enum LeadTab {
  DETAILS = 'details',
  ACTIVITIES = 'activities',
  DEMO_ACCOUNTS = 'demo-accounts',
  WELCOME_ACCOUNTS = 'welcome-accounts',
  NOTES = 'notes',
}

const ResolveLeadRoute = (): JSX.Element => {
  const [crumbs, setCrumbs] = useState([{ title: 'Leads', link: links.recordsLeads.url }])
  const leadsApiClient = useApiClient(LeadsApiClient)

  const { id, slug } = useParams<{ id: string; slug: LeadTab }>()
  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? leadsApiClient.getDetailedLead(id) : Promise.resolve(null)),
    [id, slug]
  )
  const { data: lead, isLoading } = useFetchOne(fetchData)

  const matchesSlug = (leadDetailsSlug?: LeadTab) => {
    if (!leadDetailsSlug) {
      return false
    }
    return (
      leadDetailsSlug === LeadTab.DETAILS ||
      leadDetailsSlug === LeadTab.ACTIVITIES ||
      leadDetailsSlug === LeadTab.DEMO_ACCOUNTS ||
      leadDetailsSlug === LeadTab.WELCOME_ACCOUNTS ||
      leadDetailsSlug === LeadTab.NOTES
    )
  }

  useEffect(() => {
    if (lead) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: `${lead.firstName} ${lead.lastName}`,
          link: leadDetailsRouteUrl(
            lead,
            slug ? (matchesSlug(slug) ? slug : LeadTab.DETAILS) : LeadTab.DETAILS
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [lead]) // eslint-disable-line react-hooks/exhaustive-deps

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!lead) {
      return <PageNotFound />
    }
    if (slug === LeadTab.DETAILS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <LeadDetailsPage data={lead} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (slug === LeadTab.ACTIVITIES) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <LeadActivitiesPage data={lead} />
        </React.Fragment>
      )
    }
    if (slug === LeadTab.DEMO_ACCOUNTS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <LeadDemoWelcomeAccountsPage data={lead} type={LeadTab.DEMO_ACCOUNTS} />
        </React.Fragment>
      )
    }
    if (slug === LeadTab.WELCOME_ACCOUNTS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <LeadDemoWelcomeAccountsPage data={lead} type={LeadTab.WELCOME_ACCOUNTS} />
        </React.Fragment>
      )
    }
    if (slug === LeadTab.NOTES) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <LeadDetailsNotesPage data={lead} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}

export default ResolveLeadRoute
